import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Check } from 'lucide-react';

export default function FiveKeyPractices() {
  const navigate = useNavigate();

  const practices = [
    {
      number: 1,
      title: 'Slow it down',
      description: 'Speak at a rate the listener can really get what you are saying.',
      icon: '🐢',
      color: 'from-blue-900/30 to-cyan-900/30',
      borderColor: 'border-blue-700/30'
    },
    {
      number: 2,
      title: 'Chunk it down',
      description: 'Talk about smaller bits of information so the listener can really get your meaning.',
      icon: '🧩',
      color: 'from-purple-900/30 to-pink-900/30',
      borderColor: 'border-purple-700/30'
    },
    {
      number: 3,
      title: 'Reflect back',
      description: 'Say back the essential meaning of what you heard to be sure you understand what was intended.',
      icon: '🪞',
      color: 'from-green-900/30 to-emerald-900/30',
      borderColor: 'border-green-700/30'
    },
    {
      number: 4,
      title: 'Alternate speaking',
      description: 'Go back and forth at the rate that best meets everyone\'s needs.',
      icon: '↔️',
      color: 'from-orange-900/30 to-amber-900/30',
      borderColor: 'border-orange-700/30'
    },
    {
      number: 5,
      title: 'Express gratitude',
      description: 'Say thank you whenever you are sincerely grateful.',
      icon: '🙏',
      color: 'from-rose-900/30 to-pink-900/30',
      borderColor: 'border-rose-700/30'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">5 Key Practices</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 14</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Introduction */}
          <div className="text-center mb-12">
            <Check className="h-20 w-20 text-green-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">For a Compassionate Conversation</h2>
            <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
              These five practices create the conditions for deep understanding and connection. 
              Use them together for powerful, compassionate communication.
            </p>
          </div>

          {/* Practices List */}
          <div className="space-y-6 mb-12">
            {practices.map((practice, idx) => (
              <div
                key={idx}
                className={`bg-gradient-to-br ${practice.color} border ${practice.borderColor} rounded-lg p-8 hover:scale-[1.01] transition-transform`}
                data-testid={`practice-${idx}`}
              >
                <div className="flex items-start gap-6">
                  {/* Number Circle */}
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-white/10 rounded-full flex flex-col items-center justify-center border-2 border-white/20">
                      <div className="text-4xl mb-1">{practice.icon}</div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-white/60 font-mono text-xl">#{practice.number}</span>
                      <h3 className="text-3xl font-bold text-white">{practice.title}</h3>
                    </div>
                    <p className="text-slate-200 text-lg leading-relaxed">
                      {practice.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Practice Together */}
          <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-700/30 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-6">Using All Five Together</h3>
            <div className="space-y-4 text-slate-200">
              <p className="leading-relaxed">
                <span className="font-semibold text-white">In Practice:</span> Start by slowing down your speech. 
                Break your message into small, digestible chunks. After each chunk, pause and ask the other person 
                to reflect back what they heard.
              </p>
              <p className="leading-relaxed">
                Take turns speaking and listening, going back and forth at a pace that works for both of you. 
                Throughout the conversation, express genuine gratitude for each other's willingness to be present.
              </p>
              <p className="leading-relaxed mt-4 text-slate-300 italic">
                These practices may feel awkward at first, but they become natural with practice. They're especially 
                valuable during difficult conversations when emotions are high and misunderstanding is likely.
              </p>
            </div>
          </div>

          {/* Teaching Tips */}
          <div className="mt-8 bg-blue-900/20 border border-blue-700/30 rounded-lg p-8">
            <h3 className="text-xl font-bold text-white mb-4">Teaching Tips</h3>
            <ul className="space-y-3 text-slate-200">
              <li className="flex items-start gap-3">
                <span className="text-blue-400 mt-1">•</span>
                <span>Practice these in pairs with simple, low-stakes topics first</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-400 mt-1">•</span>
                <span>Have students notice which practice is hardest for them personally</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-400 mt-1">•</span>
                <span>Emphasize that "slow" and "small" aren't signs of weakness—they're gifts of clarity</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-400 mt-1">•</span>
                <span>Reflection isn't just repeating words—it's capturing the essence of what was said</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
